/**
 * CP-5.3 — Crews & Helper Invitations frontend.
 *
 *   - LeadCrewManagement   → /app/leads/me/bookings/:id/crew
 *   - HelperInbox          → /app/helpers/me/invitations
 *   - HelperInvitationDetail → /app/helpers/me/invitations/:id
 *   - ClientCrewCard       (component) — used inside JobDetail booking row
 *   - AdminCrewsTab / AdminInvitationsTab (components) used by AdminConsole
 *
 * Workforce Partners terminology:
 *   - Project (not Job)
 *   - Lead Technician (not Lead)
 *   - Helper Technician (not Helper)
 *   - Booking Request / Crew / Invitation
 *   - Company (not Org)
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import {
  LeadTimesheetsPanel,
  LeadRateHelpersPanel,
  HelperLogHoursForm,
  HelperRateLeadForm,
  HelperTrustBlock,
} from "@/pages/Phase6";
import { LeadEscrowPanel } from "@/pages/Escrow";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

const STATUS_TONE = {
  forming: "avcp-pill",
  partial: "avcp-pill",
  ready: "avcp-pill ok",
  in_progress: "avcp-pill ok",
  completed: "avcp-pill ok",
  cancelled: "avcp-pill warn",
};

function CrewStatusPill({ status }) {
  return (
    <span className={STATUS_TONE[status] || "avcp-pill"} data-testid={TID.crewStatusPill}>
      {status}
    </span>
  );
}

// ===========================================================================
// LEAD — Crew Management
// ===========================================================================

export function LeadCrewManagement() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);

  const load = async () => {
    try {
      const r = await api(`/bookings/${id}/crew`);
      setData(r);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading) return <Layout><div className="avcp-card p-6">Loading crew…</div></Layout>;
  if (!data) return null;

  const crew = data.crew;
  const invitations = data.invitations || [];
  const sc = data.skill_coverage;
  const ready = crew && crew.crew_status === "ready";
  const inProgress = crew && crew.crew_status === "in_progress";

  return (
    <Layout>
      <div data-testid={TID.crewRoot}>
        <div className="flex items-center gap-2 mb-2">
          <button
            onClick={() => navigate(`/app/leads/me/bookings/${id}`)}
            className="text-sm text-[var(--avcp-ink-soft)] hover:underline"
          >← Back to Booking</button>
        </div>
        <h1 className="text-3xl font-bold tracking-tight mb-2">Crew Management</h1>
        <p className="text-[var(--avcp-ink-soft)] mb-6">
          Invite Helper Technicians to join your crew for this project. You own the
          crew composition — the Client never sees helper rates or negotiation history.
        </p>

        {/* Counters */}
        <div className="avcp-card p-5 mb-4" data-testid={TID.crewCounters}>
          <div className="flex items-center gap-4 flex-wrap">
            <CrewStatusPill status={crew?.crew_status || "forming"} />
            <Counter label="Total slots" value={crew?.slots_total ?? data.helper_count_requested}
                     testid={TID.crewSlotsTotal} />
            <Counter label="Filled" value={crew?.slots_filled ?? 0}
                     testid={TID.crewSlotsFilled} />
            <Counter label="Pending" value={crew?.slots_pending ?? 0}
                     testid={TID.crewSlotsPending} />
            <Counter label="Declined" value={crew?.slots_declined ?? 0} />
            <Counter label="Withdrawn" value={crew?.slots_withdrawn ?? 0} />
            <div className="ml-auto flex items-center gap-2">
              {ready && (
                <button
                  className="avcp-btn-primary"
                  data-testid={TID.crewStartWork}
                  onClick={async () => {
                    try {
                      await api(`/bookings/${id}/crew/start`, { method: "POST", body: {} });
                      toast.success("Crew work started.");
                      load();
                    } catch (e) { toast.error(e.message); }
                  }}
                >Start Work</button>
              )}
              <button
                className="avcp-btn-secondary"
                data-testid={TID.crewInviteOpen}
                onClick={() => setShowInvite(true)}
                disabled={inProgress}
              >Invite Helper Technician</button>
            </div>
          </div>
        </div>

        {/* Skill Coverage Guidance — informational only (§5.9) */}
        {sc && sc.required && sc.required.length > 0 && (
          <div className="avcp-card p-5 mb-4" data-testid={TID.crewSkillCoverage}>
            <h3 className="font-semibold mb-1">Skill Coverage</h3>
            <p className="text-xs text-[var(--avcp-ink-soft)] mb-3">
              Informational — Workforce Partners never blocks crew formation based on coverage.
              Final selection is your call.
            </p>
            <div className="flex gap-6 flex-wrap text-sm">
              <div data-testid={TID.crewSkillCovered}>
                <div className="text-[var(--avcp-ink-soft)] text-xs">Covered</div>
                <div className="flex gap-1 flex-wrap mt-1">
                  {sc.covered_skill_codes.length === 0 && <span className="avcp-pill">none yet</span>}
                  {sc.covered_skill_codes.map((c) => <span key={c} className="avcp-pill ok">{c}</span>)}
                </div>
              </div>
              <div data-testid={TID.crewSkillUncovered}>
                <div className="text-[var(--avcp-ink-soft)] text-xs">Not yet covered</div>
                <div className="flex gap-1 flex-wrap mt-1">
                  {sc.uncovered_skill_codes.length === 0 && <span className="avcp-pill ok">all covered</span>}
                  {sc.uncovered_skill_codes.map((c) => <span key={c} className="avcp-pill">{c}</span>)}
                </div>
              </div>
              {sc.extra_skill_codes && sc.extra_skill_codes.length > 0 && (
                <div>
                  <div className="text-[var(--avcp-ink-soft)] text-xs">Extra on the bench</div>
                  <div className="flex gap-1 flex-wrap mt-1">
                    {sc.extra_skill_codes.map((c) => <span key={c} className="avcp-pill">{c}</span>)}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Invitations table */}
        <div className="avcp-card p-5" data-testid={TID.crewInvitationsTable}>
          <h3 className="font-semibold mb-3">Invitations</h3>
          {invitations.length === 0 ? (
            <p className="text-sm text-[var(--avcp-ink-soft)]">
              No invitations yet. Click <em>Invite Helper Technician</em> to start.
            </p>
          ) : (
            <ul className="space-y-2">
              {invitations.map((inv) => (
                <li key={inv.id} className="border border-[var(--avcp-line)] rounded p-3 flex items-center gap-3 flex-wrap"
                    data-testid={TID.crewInvitationRow}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{inv.helper_display_name}</div>
                    <div className="text-xs text-[var(--avcp-ink-soft)]">
                      <span className="avcp-pill">{inv.state}</span>
                      <span className="ml-2">{inv.offered_currency} {inv.offered_rate}/h offered</span>
                      {inv.accepted_rate && <span className="ml-2">· {inv.accepted_currency} {inv.accepted_rate}/h accepted</span>}
                      <span className="ml-2">· created {fmt(inv.created_at)}</span>
                    </div>
                    {inv.lead_message && <p className="text-xs mt-1 italic">&ldquo;{inv.lead_message}&rdquo;</p>}
                    {/* CP-6.3 — Helper trust block (Lead-only view) */}
                    <HelperTrustBlock trust={inv.trust} />
                  </div>
                  {(inv.state === "invited" || inv.state === "pending") && (
                    <button
                      className="avcp-btn-secondary text-xs"
                      data-testid={TID.crewInviteWithdraw}
                      onClick={async () => {
                        const reason = window.prompt("Reason for withdrawing this invitation? (optional)");
                        try {
                          await api(`/bookings/${id}/crew/invitations/${inv.id}/withdraw`,
                                     { method: "POST", body: { reason } });
                          toast.success("Invitation withdrawn.");
                          load();
                        } catch (e) { toast.error(e.message); }
                      }}
                    >Withdraw</button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {showInvite && (
          <InviteSheet
            bookingId={id}
            currency={crew?.currency || "USD"}
            onClose={() => { setShowInvite(false); load(); }}
          />
        )}

        {/* CP-6.3 — Timesheets panel (Lead's own + helper approvals) */}
        {crew && ["ready", "in_progress", "completed"].includes(crew.crew_status) && (
          <LeadTimesheetsPanel
            bookingId={id}
            invitations={invitations}
            onChange={load}
          />
        )}

        {/* CP-6.3 — Rate Helper Technicians panel (post-completion) */}
        {crew && crew.crew_status === "completed" && (
          <LeadRateHelpersPanel bookingId={id} invitations={invitations} />
        )}

        {/* CP-11.2 M3 — Lead-side escrow visibility + Lock §3 release timeline */}
        <LeadEscrowPanel bookingId={id} />
      </div>
    </Layout>
  );
}

function Counter({ label, value, testid }) {
  return (
    <div className="flex flex-col items-center">
      <div className="text-2xl font-bold leading-none" data-testid={testid}>{value ?? 0}</div>
      <div className="text-xs text-[var(--avcp-ink-soft)] mt-1">{label}</div>
    </div>
  );
}

function InviteSheet({ bookingId, currency, onClose }) {
  const [uuid, setUuid] = useState("");
  const [rate, setRate] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!uuid.trim() || !rate) { toast.error("Helper UUID and offered rate are required."); return; }
    setSubmitting(true);
    try {
      await api(`/bookings/${bookingId}/crew/invitations`, {
        method: "POST",
        body: {
          helper_user_uuid: uuid.trim(),
          offered_rate: parseFloat(rate),
          lead_message: message || undefined,
        },
      });
      toast.success("Invitation sent.");
      onClose();
    } catch (e) {
      toast.error(e.problem?.code || e.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-[var(--avcp-bg)] rounded-lg max-w-md w-full p-6">
        <h3 className="font-semibold mb-3">Invite a Helper Technician</h3>
        <p className="text-xs text-[var(--avcp-ink-soft)] mb-4">
          The helper&apos;s rate is private between you and them. The Client never sees it.
        </p>
        <label className="block text-sm mb-2">
          Helper user UUID
          <input
            className="avcp-input w-full"
            data-testid={TID.crewInviteHelperUuid}
            value={uuid}
            onChange={(e) => setUuid(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
          />
        </label>
        <label className="block text-sm mb-2">
          Offered rate ({currency} / hour)
          <input
            className="avcp-input w-full"
            data-testid={TID.crewInviteRate}
            type="number" step="0.01" min="0"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
          />
        </label>
        <label className="block text-sm mb-4">
          Message to the helper (optional)
          <textarea
            className="avcp-input w-full"
            rows={3}
            data-testid={TID.crewInviteMessage}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </label>
        <div className="flex gap-2 justify-end">
          <button className="avcp-btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="avcp-btn-primary"
            data-testid={TID.crewInviteSubmit}
            disabled={submitting}
            onClick={submit}
          >{submitting ? "Sending…" : "Send invitation"}</button>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// HELPER — Inbox
// ===========================================================================

export function HelperInbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api("/helpers/me/invitations");
        setItems(r.data || []);
      } catch (e) { toast.error(e.message); } finally { setLoading(false); }
    })();
  }, []);

  return (
    <Layout>
      <div data-testid={TID.helperInboxRoot}>
        <h1 className="text-3xl font-bold tracking-tight mb-2">Helper Invitations</h1>
        <p className="text-[var(--avcp-ink-soft)] mb-6">
          Invitations from Lead Technicians to join their crews. Your rate is private between
          you and the Lead Technician — the Client never sees it. Accepting will reveal the
          site address and contacts.
        </p>
        {loading ? <div className="avcp-card p-6">Loading…</div> : (
          <ul className="space-y-3">
            {items.length === 0 && (
              <li className="avcp-card p-6 text-sm text-[var(--avcp-ink-soft)]">
              No invitations yet. Make sure your profile is set to Active so Lead Technicians can find you.
              </li>
            )}
            {items.map((inv) => (
              <li key={inv.id} className="avcp-card p-5" data-testid={TID.helperInboxRow}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <Link to={`/app/helpers/me/invitations/${inv.id}`} className="font-semibold">
                      {inv.job_title}
                    </Link>
                    <div className="text-xs text-[var(--avcp-ink-soft)] mt-1">
                      <span className="avcp-pill">{inv.state}</span>
                      <span className="ml-2">{fmt(inv.starts_at)} → {fmt(inv.ends_at)}</span>
                      <span className="ml-2">· {inv.region_label} · {inv.service_city || "—"}</span>
                      <span className="ml-2">· {inv.offered_currency} {inv.offered_rate}/h offered</span>
                    </div>
                    <div className="text-xs mt-1">
                      Lead Technician: <span className="font-medium">{inv.lead_display_name}</span>
                      {inv.client_org_name && <> · Client: {inv.client_org_name}</>}
                    </div>
                    {inv.lead_message && <p className="text-sm mt-2">&ldquo;{inv.lead_message}&rdquo;</p>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Layout>
  );
}

// ===========================================================================
// HELPER — Invitation Detail (with address reveal post-accept)
// ===========================================================================

export function HelperInvitationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api(`/helpers/me/invitations/${id}`);
      setData(r);
    } catch (e) {
      toast.error(e.message);
      navigate("/app/helpers/me/invitations");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading) return <Layout><div className="avcp-card p-6">Loading…</div></Layout>;
  if (!data) return null;

  const inv = data.invitation;
  const job = data.job;
  const revealed = data.address_revealed;

  return (
    <Layout>
      <div data-testid={TID.helperInvitationDetail}>
        <div className="flex items-center gap-2 mb-2">
          <Link to="/app/helpers/me/invitations" className="text-sm text-[var(--avcp-ink-soft)] hover:underline">
            ← Back to Invitations
          </Link>
        </div>
        <h1 className="text-3xl font-bold tracking-tight mb-2">{job.title}</h1>
        <div className="text-xs text-[var(--avcp-ink-soft)] mb-6">
          <span className="avcp-pill">{inv.state}</span>
          <span className="ml-2">Offered: {inv.offered_currency} {inv.offered_rate}/h</span>
          {inv.accepted_rate && <span className="ml-2">· Accepted: {inv.accepted_currency} {inv.accepted_rate}/h</span>}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <section className="avcp-card p-5">
            <h3 className="font-semibold mb-2">Project</h3>
            <Field label="Lead Technician" value={data.lead?.display_name} />
            {data.client_org && <Field label="Client Company" value={data.client_org.name} />}
            <Field label="Dates" value={`${fmt(job.starts_at)} → ${fmt(job.ends_at)}`} />
            <Field label="Duration kind" value={job.job_duration_kind} />
            <Field label="Currency" value={job.currency} />
            <Field label="City / region" value={job.service_city} />
            {job.description && <Field label="Description" value={job.description} />}
            {job.required_skills_json && job.required_skills_json.length > 0 && (
              <Field label="Required skills" value={job.required_skills_json.join(", ")} />
            )}
          </section>

          <section className="avcp-card p-5">
            <h3 className="font-semibold mb-2">Crew</h3>
            {data.crew ? (
              <>
                <Field label="Status" value={data.crew.crew_status} />
                <Field label="Slots" value={`${data.crew.slots_filled} of ${data.crew.slots_total} filled`} />
                <Field label="Pending invitations" value={data.crew.slots_pending} />
              </>
            ) : <p className="text-sm text-[var(--avcp-ink-soft)]">No crew details yet.</p>}
          </section>

          {revealed ? (
            <section className="avcp-card p-5 md:col-span-2" data-testid={TID.helperInvitationAddressBox}>
              <h3 className="font-semibold mb-2">Site & contacts</h3>
              <p className="text-xs text-[var(--avcp-ink-soft)] mb-3">
                Revealed because you accepted. Please keep this confidential per platform terms.
              </p>
              <Field label="Site address" value={job.service_address} />
              <Field label="Postal / city" value={job.service_postal} />
              <Field label="Meeting location" value={job.meet_location} />
              <Field label="Meeting time" value={fmt(job.meet_at)} />
              <Field label="Parking" value={job.parking_instructions} />
              <Field label="Site access" value={job.site_access_notes} />
              <Field label="Internal notes" value={job.notes_internal} />
              {data.contacts && data.contacts.length > 0 && (
                <div className="mt-3">
                  <h4 className="font-medium">Contacts</h4>
                  <ul className="text-sm space-y-1 mt-1">
                    {data.contacts.map((c) => (
                      <li key={c.id}>
                        {c.display_name} ({c.role_label}) · {c.phone || c.email}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {data.packages && data.packages.length > 0 && (
                <div className="mt-3">
                  <h4 className="font-medium">Project documents</h4>
                  <ul className="text-sm space-y-1 mt-1">
                    {data.packages.map((p) => (
                      <li key={p.id}>{p.kind} · {p.title || "(untitled)"} · v{p.version}</li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          ) : (
            <section className="avcp-card p-5 md:col-span-2">
              <h3 className="font-semibold mb-2">Make a decision</h3>
              <p className="text-xs text-[var(--avcp-ink-soft)] mb-4">
                Once you accept, the full site address, contacts, and project documents will
                be revealed. Your profile name, avatar, and primary skills become visible to
                the Client once the crew is confirmed. Your rate is never shared with the
                Client.
              </p>
              <div className="flex gap-2">
                <button
                  className="avcp-btn-primary"
                  data-testid={TID.helperInvitationAccept}
                  onClick={async () => {
                    try {
                      await api(`/helpers/me/invitations/${id}/accept`, { method: "POST", body: {} });
                      toast.success("Invitation accepted.");
                      load();
                    } catch (e) { toast.error(e.message); }
                  }}
                >Accept</button>
                <button
                  className="avcp-btn-secondary"
                  data-testid={TID.helperInvitationDecline}
                  onClick={async () => {
                    const reason = window.prompt("Reason for declining? (optional)");
                    try {
                      await api(`/helpers/me/invitations/${id}/decline`,
                                 { method: "POST", body: { reason } });
                      toast.success("Invitation declined.");
                      navigate("/app/helpers/me/invitations");
                    } catch (e) { toast.error(e.message); }
                  }}
                >Decline</button>
              </div>
            </section>
          )}

          {revealed && inv.state === "accepted" && (
            <section className="avcp-card p-5 md:col-span-2">
              <h3 className="font-semibold mb-2">Withdraw</h3>
              <p className="text-xs text-[var(--avcp-ink-soft)] mb-3">
                You may withdraw if the project start is more than 24 hours away (admin-configurable).
              </p>
              <button
                className="avcp-btn-secondary"
                data-testid={TID.helperInvitationWithdraw}
                onClick={async () => {
                  const reason = window.prompt("Reason for withdrawing? (optional)");
                  try {
                    await api(`/helpers/me/invitations/${id}/withdraw`,
                               { method: "POST", body: { reason } });
                    toast.success("Invitation withdrawn.");
                    navigate("/app/helpers/me/invitations");
                  } catch (e) {
                    toast.error(e.problem?.title || e.message);
                  }
                }}
              >Withdraw from this invitation</button>
            </section>
          )}

          {/* CP-6.3 — Log hours (visible when accepted; backend further gates on work_status) */}
          {revealed && inv.state === "accepted" && (
            <HelperLogHoursForm
              invitationId={id}
              invitation={inv}
              onSaved={load}
            />
          )}

          {/* CP-6.3 — Rate Lead Technician (only when booking completed + own work eligible) */}
          {revealed && (
            <HelperRateLeadForm
              invitationId={id}
              invitation={inv}
              bookingState={data.booking_state}
            />
          )}
        </div>
      </div>
    </Layout>
  );
}

function Field({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="text-sm py-1">
      <span className="text-[var(--avcp-ink-soft)]">{label}: </span>
      <span>{String(value)}</span>
    </div>
  );
}

// ===========================================================================
// CLIENT — Crew Card (used inside JobDetail)
// ===========================================================================

export function ClientCrewCard({ jobId, bookingId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api(`/jobs/${jobId}/bookings/${bookingId}/crew-summary`);
        setData(r);
      } catch (e) {
        // Crew summary may 404 if the booking has no crew yet — that's expected
        setData(null);
      } finally { setLoading(false); }
    })();
  }, [jobId, bookingId]);

  if (loading) return null;
  if (!data || !data.crew) return null;

  const c = data.crew;
  const roster = data.crew_roster;
  const showRoster = data.booking_state === "crew_assigned" && roster && roster.length > 0;

  return (
    <div className="avcp-card p-5" data-testid={TID.clientCrewCard}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="font-semibold">Crew</h3>
        <CrewStatusPill status={c.status} />
      </div>
      <div className="flex items-center gap-4 mb-3 text-sm">
        <Counter label="Slots" value={`${c.slots_filled}/${c.slots_total}`} />
        <Counter label="Pending" value={c.slots_pending} />
        <Counter label="Open" value={c.slots_open} />
      </div>
      <p className="text-xs text-[var(--avcp-ink-soft)] mb-3">
        Your Lead Technician owns crew composition. You&apos;ll see who is coming to site once the crew is confirmed.
      </p>
      {showRoster && (
        <div data-testid={TID.clientCrewRoster}>
          <h4 className="font-medium text-sm mb-2">Coming to site</h4>
          <ul className="space-y-2">
            {roster.map((m) => (
              <li key={m.helper_user_uuid} className="flex items-center gap-3 border border-[var(--avcp-line)] rounded p-2"
                  data-testid={TID.clientCrewRosterRow}>
                <div className="w-9 h-9 rounded-full bg-[var(--avcp-surface-2)] flex items-center justify-center text-xs font-bold">
                  {(m.display_name || "?").slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{m.display_name || "Helper Technician"}</div>
                  <div className="text-xs text-[var(--avcp-ink-soft)] flex items-center gap-2 flex-wrap">
                    <span>{m.role_label}</span>
                    <span>· {m.company_badge}</span>
                    {m.primary_skill_codes && m.primary_skill_codes.length > 0 && (
                      <span>· {m.primary_skill_codes.join(", ")}</span>
                    )}
                  </div>
                </div>
                <div className="text-xs text-[var(--avcp-ink-soft)]">#{m.slot_position}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// CLIENT — Booking PO field
// ===========================================================================

export function BookingPoField({ jobId, booking, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(booking.client_po_number || "");
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const locked = !!booking.client_po_locked_at;
  const source = booking.client_po_source;

  const save = async () => {
    const v = value.trim();
    if (!v) { toast.error("PO must not be empty."); return; }
    setSaving(true);
    try {
      const r = await api(`/jobs/${jobId}/bookings/${booking.id}/po`, {
        method: "PATCH",
        body: { client_po_number: v },
      });
      toast.success("Purchase order updated.");
      setEditing(false);
      if (onUpdate) onUpdate({ ...booking, ...r });
    } catch (e) {
      toast.error(e.problem?.title || e.message);
    } finally { setSaving(false); }
  };

  const regenerate = async () => {
    if (!window.confirm(
      "Regenerate the system PO? This replaces the current PO number with a fresh Workforce Partners reference."
    )) return;
    setRegenerating(true);
    try {
      const r = await api(`/jobs/${jobId}/bookings/${booking.id}/po/regenerate`, {
        method: "POST",
        body: {},
      });
      toast.success(`Purchase order regenerated: ${r.client_po_number}`);
      if (onUpdate) onUpdate({ ...booking, client_po_number: r.client_po_number,
                                client_po_source: "system_generated" });
    } catch (e) {
      const code = e.problem?.code;
      if (code === "PO_LOCKED") {
        toast.error("PO is locked — admin remediation required to change it.");
      } else {
        toast.error(e.problem?.title || e.message);
      }
    } finally { setRegenerating(false); }
  };

  return (
    <div className="avcp-card p-4" data-testid={TID.bookingPoField}>
      <div className="text-xs text-[var(--avcp-ink-soft)] mb-1">Purchase Order</div>
      {!editing ? (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-sm">{booking.client_po_number || "—"}</span>
          {source && (
            <span className="avcp-pill" data-testid={TID.bookingPoBadge}>
              {locked ? "Locked" : (source === "client_edited" ? "Edited" : "Auto-generated")}
            </span>
          )}
          {!locked && (
            <div className="flex items-center gap-1.5 ml-auto">
              <button
                className="avcp-btn-secondary text-xs"
                data-testid={TID.bookingPoEdit}
                onClick={() => { setValue(booking.client_po_number || ""); setEditing(true); }}
              >Edit</button>
              <button
                className="avcp-btn-secondary text-xs"
                data-testid="booking-po-regenerate"
                disabled={regenerating}
                onClick={regenerate}
              >{regenerating ? "Regenerating…" : "Regenerate"}</button>
            </div>
          )}
          {locked && (
            <span className="text-xs text-[var(--avcp-ink-soft)] ml-auto" title="Locked — an invoice has been issued.">🔒</span>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 mt-2">
          <input
            className="avcp-input flex-1"
            data-testid={TID.bookingPoInput}
            value={value}
            maxLength={64}
            onChange={(e) => setValue(e.target.value)}
          />
          <button
            className="avcp-btn-primary"
            data-testid={TID.bookingPoSave}
            disabled={saving}
            onClick={save}
          >{saving ? "Saving…" : "Save"}</button>
          <button className="avcp-btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// ADMIN — Crews + Invitations observability tables
// ===========================================================================

export function AdminCrewsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api("/admin/crews");
        setRows(r.data || []);
      } catch (e) { toast.error(e.message); } finally { setLoading(false); }
    })();
  }, []);
  if (loading) return <div>Loading crews…</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={TID.adminCrewsTable}>
        <thead>
          <tr className="text-left text-xs text-[var(--avcp-ink-soft)]">
            <th>Project</th><th>Lead</th><th>Client</th><th>Status</th>
            <th>Slots</th><th>Created</th><th>Events</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={7} className="py-3 text-[var(--avcp-ink-soft)]">No crews yet.</td></tr>}
          {rows.map((r) => (
            <tr key={r.crew_id} data-testid={TID.adminCrewsRow} className="border-t border-[var(--avcp-line)]">
              <td className="py-2">{r.job_title}</td>
              <td>{r.lead_email}</td>
              <td>{r.client_email}</td>
              <td><CrewStatusPill status={r.crew_status} /></td>
              <td>{r.slots_filled} / {r.slots_total}</td>
              <td>{fmt(r.created_at)}</td>
              <td>{r.audit_events}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AdminInvitationsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api("/admin/invitations");
        setRows(r.data || []);
      } catch (e) { toast.error(e.message); } finally { setLoading(false); }
    })();
  }, []);
  if (loading) return <div>Loading invitations…</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={TID.adminInvitationsTable}>
        <thead>
          <tr className="text-left text-xs text-[var(--avcp-ink-soft)]">
            <th>Project</th><th>Lead</th><th>Helper</th><th>State</th>
            <th>Offered</th><th>Accepted</th><th>Created</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={7} className="py-3 text-[var(--avcp-ink-soft)]">No invitations yet.</td></tr>}
          {rows.map((r) => (
            <tr key={r.invitation_id} data-testid={TID.adminInvitationsRow} className="border-t border-[var(--avcp-line)]">
              <td className="py-2">{r.job_title}</td>
              <td>{r.lead_email}</td>
              <td>{r.helper_email}</td>
              <td><span className="avcp-pill">{r.state}</span></td>
              <td>{r.offered_currency} {r.offered_rate}</td>
              <td>{r.accepted_rate ? `${r.accepted_currency} ${r.accepted_rate}` : "—"}</td>
              <td>{fmt(r.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
